import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, User, AlertTriangle, CheckCircle, RefreshCw, Loader2 } from 'lucide-react';
import notificationService from '../../../services/notificationService';
import { toast } from 'react-hot-toast';

const NotificationTester = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);

  useEffect(() => {
    fetchNotifications();
    fetchBadgeCount();
    fetchEventTypes();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications();
      setNotifications(response.results || []);
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchBadgeCount = async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch badge count:', error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const types = await notificationService.getEventTypes();
      setEventTypes(types);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const markAsRead = async (notificationIds) => {
    try {
      await notificationService.markAsRead(notificationIds);
      fetchNotifications();
      fetchBadgeCount();
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      fetchNotifications();
      fetchBadgeCount();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const triggerTestNotification = async (eventType) => {
    try {
      // Create a real test notification via API
      const { default: api } = await import('../../../services/api');
      
      // For test notifications, navigate to safe fallback pages instead of fake IDs
      const testPayloads = {
        'participant.created': {
          participant_number: 'P001',
          site_code: '0001',
          // Don't include fake IDs - will fall back to no navigation for tests
        },
        'survey.missed': {
          survey_name: 'Daily Pain Assessment',
          participant_number: 'P002', 
          site_code: '0002',
          // Don't include fake IDs - will fall back to no navigation for tests
        },
        'dcq.updated': {
          survey_name: 'Protocol Deviation Review',
          // Don't include fake IDs - will fall back to no navigation for tests  
        }
      };

      await api.post('/api/dev/create-test-notification/', {
        event_type: eventType,
        payload: testPayloads[eventType] || {}
      });

      toast.success(`Test notification created: ${eventType}`);
      
      // Wait a moment then refresh
      setTimeout(() => {
        fetchNotifications();
        fetchBadgeCount();
      }, 500);
    } catch (error) {
      console.error('Failed to create test notification:', error);
      toast.error(`Failed to create test notification: ${error.response?.data?.error || error.message}`);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div>
      <h3 className="mb-5 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
        <Bell className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        Notification System Tester
        {unreadCount > 0 && (
          <span className="badge badge-danger ml-1">
            {unreadCount} unread
          </span>
        )}
      </h3>
      
      <div className="space-y-6">
        {/* Test Trigger Buttons */}
        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Test Notification Events</h4>
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={() => triggerTestNotification('participant.created')}
              className="btn btn-secondary btn-sm"
            >
              <User className="h-3.5 w-3.5 text-gray-400" />
              Participant Created
            </button>
            <button 
              onClick={() => triggerTestNotification('survey.missed')}
              className="btn btn-secondary btn-sm"
            >
              <AlertTriangle className="h-3.5 w-3.5 text-warning-600 dark:text-warning-400" />
              Survey Missed
            </button>
            <button 
              onClick={() => triggerTestNotification('dcq.updated')}
              className="btn btn-secondary btn-sm"
            >
              <CheckCircle className="h-3.5 w-3.5 text-success-600 dark:text-success-400" />
              DCQ Updated
            </button>
            <button 
              onClick={fetchNotifications}
              className="btn btn-ghost btn-sm"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Actions */}
        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Bulk Actions</h4>
          <div className="flex gap-2">
            <button 
              onClick={markAllAsRead}
              className="btn btn-primary btn-sm"
              disabled={unreadCount === 0}
            >
              Mark All Read ({unreadCount})
            </button>
          </div>
        </div>

        {/* Event Types List */}
        {eventTypes.length > 0 && (
          <div>
            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Available Event Types</h4>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {eventTypes.map(eventType => (
                <div 
                  key={eventType.key}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40"
                >
                  <div className="font-mono text-xs font-medium text-gray-900 dark:text-gray-100">{eventType.key}</div>
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">{eventType.description}</div>
                  <div className="mt-2 flex items-center">
                    <span className={`badge ${
                      eventType.default_enabled 
                        ? 'badge-success' 
                        : 'badge-gray'
                    }`}>
                      {eventType.default_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    {eventType.audience_hint && (
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        Target: {eventType.audience_hint}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Recent Notifications</h4>
          {loading ? (
            <div className="py-4 text-center">
              <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center">
              <Bell className="mx-auto mb-2 h-6 w-6 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
              <p className="text-sm text-gray-500 dark:text-gray-400">No notifications yet</p>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Trigger a test notification above</p>
            </div>
          ) : (
            <div className="max-h-96 space-y-2 overflow-y-auto">
              {notifications.map(notification => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className={`rounded-lg border p-3 ${
                    notification.is_read 
                      ? 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40' 
                      : 'border-primary-200 bg-primary-50/60 dark:border-primary-800/50 dark:bg-primary-900/15'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className={`text-sm font-semibold ${
                          notification.is_read ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {notification.title}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatTimeAgo(notification.created_at)}
                          </span>
                          {!notification.is_read && (
                            <div className="h-2 w-2 rounded-full bg-primary-500"></div>
                          )}
                        </div>
                      </div>
                      
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {notification.body}
                      </p>
                      
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="code-chip">
                            {notification.event_type_key}
                          </span>
                          
                          {notification.actor_name && notification.actor_name !== 'System' && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              by {notification.actor_name}
                            </span>
                          )}
                        </div>
                        
                        {!notification.is_read && (
                          <button
                            onClick={() => markAsRead([notification.id])}
                            className="link text-xs"
                          >
                            Mark Read
                          </button>
                        )}
                      </div>

                      {/* Context badges */}
                      {(notification.payload?.site_code || notification.payload?.participant_number) && (
                        <div className="mt-2 flex items-center gap-2">
                          {notification.payload?.site_code && (
                            <span className="badge badge-gray">
                              Site {notification.payload.site_code}
                            </span>
                          )}
                          {notification.payload?.participant_number && (
                            <span className="badge badge-brand">
                              P{notification.payload.participant_number}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationTester;
